import { useState } from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Sparkles } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { INSTALL_UI as UI } from "@/data/installI18n";
import { useLatestApk } from "@/hooks/useLatestApk";
import { dismissedUpdateCode, dismissUpdate, isPlayStoreApp } from "@/lib/apkVersion";
import { fill } from "@/lib/utils";

// Dashboard banner for APK users when /downloads/latest.json advertises a newer versionCode.
export function ApkUpdateBanner() {
  const { tc } = useLang();
  const { latest, updateAvailable } = useLatestApk();
  const [dismissed, setDismissed] = useState(dismissedUpdateCode);

  if (isPlayStoreApp() || !updateAvailable || dismissed >= latest.versionCode) return null;

  const later = () => { dismissUpdate(latest.versionCode); setDismissed(latest.versionCode); };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <Card className="rounded-3xl border-primary/40 bg-primary/5 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="apk-update-banner" data-latest-code={latest.versionCode}>
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-primary"><Sparkles className="h-6 w-6" /></span>
        <div className="min-w-0 flex-1">
          <p className="font-medium" data-testid="apk-update-title">{fill(tc(UI.updateTitle), { v: latest.version })}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{tc(UI.updateHint)}</p>
          <p className="text-xs text-muted-foreground mt-1">{tc(UI.updateSteps)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a href={latest.path} download data-testid="apk-update-download">
            <Button className="h-10 rounded-full px-5"><Download className="me-2 h-4 w-4" />{fill(tc(UI.updateBtn), { mb: latest.sizeMb })}</Button>
          </a>
          <button onClick={later} data-testid="apk-update-later" className="h-10 rounded-full px-4 text-sm font-medium text-muted-foreground hover:bg-background/80">{tc(UI.updateLater)}</button>
        </div>
      </Card>
    </motion.div>
  );
}
